import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { MatSnackBar } from '@angular/material';
import { finalize } from 'rxjs/operators';
import { AuthenticationService } from '@app/core';
import { WhatsappBotService } from './services/whatsapp-bot.service';
import {
  Feature,
  FeatureFormValue,
  BotSettingsResponse,
  PhoneNumberInfo,
  Template,
  ApiFeature
} from './interfaces/whatsapp-bot.interface';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TemplateEditModalComponent } from './template-edit-modal/template-edit-modal.component';

declare const FB: any;

@Component({
  selector: 'app-whatsapp-bot',
  templateUrl: './whatsapp-bot.component.html',
  styleUrls: ['./whatsapp-bot.component.scss']
})
export class WhatsappBotComponent implements OnInit, OnDestroy {
  whatsappId: string | null = null;
  wabaId: string | null = null;
  botStatusForm: FormGroup;
  informationForm: FormGroup;
  featureForm: FormGroup;
  isSaving = false;
  features: ApiFeature[] = [];
  featureMetaMap: { [key: string]: ApiFeature } = {};
  isClientFound: boolean = true;
  loadingFeatureId: string | null = null;
  phoneNumberInfo: PhoneNumberInfo | null = null;
  templates: Template[] = [];
  isLoadingTemplates = false;
  isLoadingInformation = true;
  private fbSDKLoaded = false;
  private messageListener: ((event: MessageEvent) => void) | null = null;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private whatsAppBotService: WhatsappBotService,
    private authService: AuthenticationService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.botStatusForm = this.fb.group({
      enabled: [false]
    });

    // Initialize with empty form, will be built after features are loaded
    this.featureForm = this.fb.group({
      features: this.fb.array([])
    });

    this.informationForm = this.fb.group({
      restaurantContext: [''],
      botInstructions: ['']
    });
    this.loadFeatures();
  }

  loadFeatures(): void {
    this.whatsAppBotService.getFeatures().subscribe({
      next: (features: ApiFeature[]) => {
        this.features = features || [];
        this.featureMetaMap = {};
        this.features.forEach(feature => {
          this.featureMetaMap[feature.key] = feature;
        });
        this.buildFeatureForm();
        this.loadInformation();
      },
      error: (err: any) => {
        console.error('Failed to load features:', err);
        this.loadInformation();
      }
    });
  }

  buildFeatureForm(): void {
    const featureFormArray = this.fb.array([]);
    this.features.forEach(feature => {
      featureFormArray.push(
        this.fb.group({
          featureKey: feature.key,
          enabled: false
        })
      );
    });
    this.featureForm = this.fb.group({
      features: featureFormArray
    });
  }

  ngOnDestroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }

  loadFacebookSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.fbSDKLoaded && typeof FB !== 'undefined') {
        resolve();
        return;
      }

      if (document.getElementById('facebook-jssdk')) {
        this.fbSDKLoaded = true;
        resolve();
        return;
      }

      (window as any).fbAsyncInit = () => {
        FB.init({
          appId: '1384487426075832',
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v24.0'
        });
        this.fbSDKLoaded = true;
        resolve();
      };

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => reject(new Error('Failed to load Facebook SDK'));

      document.body.appendChild(script);
    });
  }

  handleConnect(): void {
    this.loadFacebookSDK()
      .then(() => {
        this.launchEmbeddedSignup();
      })
      .catch(error => {
        console.error('[WhatsApp Connect] Failed to load Facebook SDK:', error);
        this.snackBar.open('Fehler beim Laden des Facebook SDK', '', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
      });
  }

  private launchEmbeddedSignup(): void {
    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') {
        return;
      }

      let data: any;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (e) {
        return;
      }

      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        if (data.event === 'FINISH') {
          const { phone_number_id, waba_id, business_id } = data.data;

          window.removeEventListener('message', this.messageListener!);
          this.messageListener = null;

          this.whatsAppBotService
            .sendWhatsAppSignupData({
              phoneNumberId: phone_number_id,
              wabaId: waba_id,
              businessPortfolioId: business_id,
              name: (this.authService.client && this.authService.client.name) || ''
            })
            .subscribe({
              next: () => {
                this.snackBar.open('Erfolgreich verbunden!', '', {
                  duration: 3000,
                  panelClass: ['snackbar-success']
                });
                // Update wabaId immediately
                this.wabaId = waba_id;
                this.loadInformation();
              },
              error: () => {
                this.snackBar.open('Fehler beim Verbinden mit WhatsApp', '', {
                  duration: 3000,
                  panelClass: ['snackbar-error']
                });
              }
            });
        } else if (data.event === 'CANCEL') {
          const { current_step } = data.data;
          console.warn('WhatsApp signup cancelled at step:', current_step);
          this.snackBar.open('WhatsApp-Anmeldung abgebrochen', '', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
        } else if (data.event === 'ERROR') {
          const { error_message } = data.data;
          console.error('WhatsApp-Anmeldung fehlgeschlagen:', error_message);
          this.snackBar.open('Fehler: ' + error_message, '', {
            duration: 5000,
            panelClass: ['snackbar-error']
          });
        }
      }
    };

    window.addEventListener('message', this.messageListener);

    FB.login(
      (response: any) => {
        if (!response.authResponse) {
          if (this.messageListener) {
            window.removeEventListener('message', this.messageListener);
            this.messageListener = null;
          }
          this.snackBar.open('Facebook-Anmeldung erforderlich', '', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
        }
      },
      {
        config_id: '754120284411507',
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          version: 'v3'
        }
      }
    );
  }

  loadInformation(): void {
    this.isLoadingInformation = true;
    this.whatsAppBotService.getWhatAppBotData().subscribe({
      next: res => {
        this.botStatusForm.patchValue({
          enabled: res.botEnabled || false
        });

        if (Array.isArray(res.features)) {
          this.patchFeatures(res.features);
        }

        this.informationForm.patchValue({
          restaurantContext: res.restaurantContext || '',
          botInstructions: res.botInstructions || ''
        });

        this.phoneNumberInfo = res.phoneNumberInfo || null;
        this.wabaId = res.wabaId || null;

        // Load templates if wabaId exists (bot is connected)
        if (this.wabaId) {
          this.loadTemplates();
        } else {
          this.templates = [];
        }
        this.isLoadingInformation = false;
      },
      error: (err: any) => {
        this.isClientFound = false;
        this.isLoadingInformation = false;
      }
    });
  }

  loadTemplates(): void {
    this.isLoadingTemplates = true;
    this.whatsAppBotService.getTemplates().subscribe({
      next: (templates: Template[]) => {
        this.templates = templates || [];
        this.isLoadingTemplates = false;
      },
      error: (err: any) => {
        this.isLoadingTemplates = false;
        this.snackBar.open('Fehler beim Laden der Vorlagen', '', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  openEditTemplateModal(template: Template): void {
    const modalRef = this.modalService.open(TemplateEditModalComponent, {
      size: 'lg',
      windowClass: 'template-edit-modal'
    });

    modalRef.componentInstance.templateName =
      (template.template && template.template.name) || template.metaTemplateName || 'Vorlage';
    modalRef.componentInstance.templateType = (template.template && template.template.type) || '';
    modalRef.componentInstance.content = (template.template && template.template.content) || '';

    modalRef.result.then(
      result => {
        if (result.success) {
          this.saveTemplate(result.templateType, result.content);
        }
      },
      () => {
        // Modal dismissed
      }
    );
  }

  saveTemplate(templateType: string, content: string): void {
    this.whatsAppBotService.updateTemplate(templateType, content).subscribe({
      next: () => {
        this.snackBar.open('Vorlage erfolgreich gespeichert', '', {
          duration: 3000,
          panelClass: ['snackbar-success']
        });
        this.loadTemplates();
      },
      error: (err: any) => {
        this.snackBar.open('Fehler beim Speichern der Vorlage', '', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'approved':
        return 'green';
      case 'pending':
        return 'orange';
      case 'rejected':
        return 'red';
      case 'disabled':
        return 'gray';
      default:
        return 'gray';
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'danger';
      case 'disabled':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  getTemplateName(template: Template): string {
    return (template.template && template.template.name) || template.metaTemplateName || 'Unbenannte Vorlage';
  }

  getTemplateType(template: Template): string {
    return (template.template && template.template.type) || 'N/A';
  }

  getTemplateLanguage(template: Template): string {
    return (template.template && template.template.language) || 'N/A';
  }

  hasTemplateCategory(template: Template): boolean {
    return !!(template.template && template.template.category);
  }

  getTemplateCategory(template: Template): string {
    return (template.template && template.template.category) || '';
  }

  hasTemplateDescription(template: Template): boolean {
    return !!(template.template && template.template.description);
  }

  getTemplateDescription(template: Template): string {
    return (template.template && template.template.description) || '';
  }

  hasTemplateType(template: Template): boolean {
    return !!(template.template && template.template.type);
  }

  get isConnected(): boolean {
    return !!this.wabaId;
  }

  patchFeatures(apiFeatures: BotSettingsResponse['features']): void {
    this.featuresArray.controls.forEach((control: any) => {
      const key = control.get('featureKey').value;

      const matched = apiFeatures.find((feature: any) => feature.featureKey === key);

      if (matched) {
        control.patchValue({
          enabled: matched.enabled || false
        });
      }
    });
  }

  onBotStatusToggle(): void {
    this.whatsAppBotService
      .toggleBotStatus(this.botStatusForm.value)
      .pipe(finalize(() => (this.loadingFeatureId = null)))
      .subscribe({
        next: (resp: any) => {
          this.snackBar.open('Daten gespeichert', '', { duration: 3000, panelClass: ['snackbar-success'] });
          // Templates visibility is based on wabaId, not botEnabled, so no need to reload here
        },
        error: (err: any) => {
          this.botStatusForm.patchValue({ enabled: !this.botStatusForm.value });
          this.snackBar.open('Speichern fehlgeschlagen', '', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
        }
      });
  }

  getIcon(iconKey: string): string {
    const iconMap: { [key: string]: string } = {
      calendar: 'event_available',
      menu: 'restaurant_menu',
      clock: 'schedule',
      schedule: 'schedule',
      restaurant_menu: 'restaurant_menu',
      event_available: 'event_available'
    };
    return iconMap[iconKey] || iconKey || 'extension';
  }

  getFeatureMeta(featureKey: string): ApiFeature | null {
    return this.featureMetaMap[featureKey] || null;
  }

  get featuresArray(): FormArray {
    return this.featureForm.controls.features as FormArray;
  }

  onFeatureToggle(feature: FeatureFormValue, index: number): void {
    const featuresArray = this.featureForm.get('features') as FormArray;
    const featureGroup = featuresArray.at(index) as FormGroup;

    const previousValue = featureGroup.get('enabled').value;
    this.loadingFeatureId = feature.featureKey;

    this.whatsAppBotService
      .postFeatureData(this.featureForm.value)
      .pipe(finalize(() => (this.loadingFeatureId = null)))
      .subscribe({
        next: () => {
          this.snackBar.open('Daten gespeichert', '', {
            duration: 3000,
            panelClass: ['snackbar-success']
          });
        },
        error: () => {
          featureGroup.patchValue({ enabled: !previousValue }, { emitEvent: false });

          this.snackBar.open('Speichern fehlgeschlagen', '', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
        }
      });
  }

  saveInformation(): void {
    if (this.informationForm.invalid || this.isSaving) {
      return;
    }
    this.isSaving = true;
    this.whatsAppBotService.postInformationData(this.informationForm.value).subscribe({
      next: () => {
        this.isSaving = false;
        this.snackBar.open('Informationen gespeichert', '', { duration: 3000, panelClass: ['snackbar-success'] });
      },
      error: () => {
        this.isSaving = false;
        this.snackBar.open('Speichern der Informationen fehlgeschlagen', '', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }
}
